import React, { useState, useEffect } from 'react';
import { X, Plus, Edit, Trash2, Copy, Repeat, Clock, Users, Tag, Search, Filter } from 'lucide-react';
import { useAuthStore, api } from '../auth/authStore';
import toast from 'react-hot-toast';
import { TaskTemplateModal } from './TaskTemplateModal';

interface TaskTemplatesManagerProps {
  isOpen: boolean;
  onClose: () => void;
  onCreateFromTemplate?: (templateId: string, clientId: string) => void;
}

interface TaskTemplate {
  id: string;
  name: string;
  description: string;
  type: string;
  priority: 'low' | 'medium' | 'high' | 'urgent';
  estimatedHours: number;
  billing: {
    feeType: string;
    fixedAmount?: number;
    billableRate?: number;
  };
  checklist: Array<{
    label: string;
    required: boolean;
    order: number;
  }>;
  tags: string[];
  defaultAssignees: {
    preparerId?: string;
    reviewerId?: string;
    submitterId?: string;
  };
  isRecurring: boolean;
  recurringConfig?: {
    frequency: 'WEEKLY' | 'BI_WEEKLY' | 'MONTHLY' | 'QUARTERLY' | 'ANNUALLY';
    interval: number;
    dayOfWeek?: number;
    dayOfMonth?: number;
    monthOfYear?: number;
    endDate?: string;
    maxOccurrences?: number;
  };
  createdAt: string;
  usageCount: number;
}

interface Client {
  id: string;
  displayName: string;
  legalName: string;
}

export const TaskTemplatesManager: React.FC<TaskTemplatesManagerProps> = ({
  isOpen,
  onClose,
  onCreateFromTemplate
}) => {
  const { user, tenant } = useAuthStore();
  const [templates, setTemplates] = useState<TaskTemplate[]>([]);
  const [clients, setClients] = useState<Client[]>([]);
  const [loading, setLoading] = useState(false);
  const [initializingWorkflows, setInitializingWorkflows] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [typeFilter, setTypeFilter] = useState('all');
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState<TaskTemplate | null>(null);
  const [showCreateTaskModal, setShowCreateTaskModal] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState<TaskTemplate | null>(null);
  const [selectedClientId, setSelectedClientId] = useState('');

  useEffect(() => {
    console.log('🔄 TaskTemplatesManager useEffect triggered, isOpen:', isOpen);
    if (isOpen) {
      fetchTemplates();
      fetchClients();
    }
  }, [isOpen]);

  const fetchTemplates = async () => {
    setLoading(true);
    try {
      console.log('🔄 Fetching task templates...');
      console.log('🔍 Current user:', user);
      console.log('🔍 Current tenant:', tenant);
      const response = await api.get('/firm-admin/task-templates');
      console.log('📥 API Response:', response.data);
      console.log('📥 Full Response Object:', response);

      if (response.data.success) {
        // The API returns paginated data: response.data.data.templates contains the actual templates array
        const apiData = response.data.data || {};
        console.log('📊 API Data:', apiData);
        const templatesData = apiData.templates || [];
        console.log('📋 Templates Data:', templatesData);
        console.log('📋 Templates Data Length:', templatesData.length);
        console.log('📋 Templates Data Type:', Array.isArray(templatesData));

        // Map _id to id for compatibility with the component
        const mappedTemplates = Array.isArray(templatesData) ? templatesData.map((template) => ({
          ...template,
          id: template._id || template.id,
          tags: Array.isArray(template.tags) ? template.tags : []
        })) : [];

        console.log('✅ Mapped Templates:', mappedTemplates);
        console.log('✅ Mapped Templates Count:', mappedTemplates.length);
        setTemplates(mappedTemplates);
      } else {
        console.log('❌ API Response not successful:', response.data);
        setTemplates([]);
      }
    } catch (error) {
      console.error('❌ Error fetching templates:', error);
      console.error('❌ Error details:', error.response?.data);
      console.error('❌ Error status:', error.response?.status);
      setTemplates([]);
    } finally {
      setLoading(false);
    }
  };

  const fetchClients = async () => {
    try {
      const response = await api.get('/clients');
      if (response.data.success) {
        setClients(response.data.data.clients || []);
      }
    } catch (error) {
      console.error('Error fetching clients:', error);
    }
  };

  const handleInitializeWorkflows = async () => {
    setInitializingWorkflows(true);
    try {
      const response = await api.post('/firm-admin/task-templates/initialize-workflows');
      if (response.data.success) {
        toast.success(response.data.message || 'Workflows initialized successfully');
        fetchTemplates();
      }
    } catch (error: any) {
      console.error('Error initializing workflows:', error);
      const errorMessage = error.response?.data?.message || 'Failed to initialize workflows';
      toast.error(errorMessage);
    } finally {
      setInitializingWorkflows(false);
    }
  };

  const handleDeleteTemplate = async (templateId: string) => {
    if (!confirm('Are you sure you want to delete this template?')) return;

    try {
      const response = await api.delete(`/firm-admin/task-templates/${templateId}`);
      if (response.data.success) {
        toast.success('Template deleted successfully');
        fetchTemplates();
      }
    } catch (error: any) {
      console.error('Error deleting template:', error);
      toast.error('Failed to delete template');
    }
  };

  const handleDuplicateTemplate = async (template: TaskTemplate) => {
    try {
      const duplicateData = {
        ...template,
        name: `${template.name} (Copy)`,
        id: undefined
      };

      const response = await api.post('/firm-admin/task-templates', duplicateData);
      if (response.data.success) {
        toast.success('Template duplicated successfully');
        fetchTemplates();
      }
    } catch (error: any) {
      console.error('Error duplicating template:', error);
      toast.error('Failed to duplicate template');
    }
  };

  const handleCreateFromTemplate = (template: TaskTemplate) => {
    setSelectedTemplate(template);
    setShowCreateTaskModal(true);
  };

  const handleCreateTask = async () => {
    if (!selectedTemplate || !selectedClientId) {
      toast.error('Please select a client');
      return;
    }

    try {
      const taskData = {
        type: selectedTemplate.type,
        title: `${selectedTemplate.name} - ${clients.find(c => c.id === selectedClientId)?.displayName}`,
        description: selectedTemplate.description,
        clientId: selectedClientId,
        priority: selectedTemplate.priority,
        billing: selectedTemplate.billing,
        checklist: selectedTemplate.checklist,
        tags: selectedTemplate.tags,
        assignees: selectedTemplate.defaultAssignees,
        schedule: {
          frequency: 'ONE_TIME',
          dueDate: new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toISOString() // 7 days from now
        }
      };

      const response = await api.post('/firm-admin/tasks', taskData);
      if (response.data.success) {
        toast.success('Task created from template successfully');
        setShowCreateTaskModal(false);
        setSelectedTemplate(null);
        setSelectedClientId('');
        if (onCreateFromTemplate) {
          onCreateFromTemplate(selectedTemplate.id, selectedClientId);
        }
      }
    } catch (error: any) {
      console.error('Error creating task from template:', error);
      toast.error('Failed to create task from template');
    }
  };

  // Ensure templates is always an array
  const safeTemplates = Array.isArray(templates) ? templates : [];
  console.log('🔍 Templates state:', {
    templates,
    safeTemplates,
    isArray: Array.isArray(templates),
    templatesLength: safeTemplates.length,
    firstTemplate: safeTemplates[0]
  });

  const filteredTemplates = safeTemplates.filter(template => {
    // Safe access to template properties
    const name = template.name || '';
    const description = template.description || '';
    const tags = Array.isArray(template.tags) ? template.tags : [];
    const type = template.type || '';

    // Simple logic: if search is empty, show all; if type is 'all', match all types
    const matchesSearch = searchTerm.trim() === '' ||
                         name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         description.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         (tags.length > 0 && tags.some(tag => (tag || '').toLowerCase().includes(searchTerm.toLowerCase())));

    const matchesType = typeFilter === 'all' || type === typeFilter;

    return matchesSearch && matchesType;
  });

  console.log('🔍 Filtered templates result:', {
    originalCount: safeTemplates.length,
    filteredCount: filteredTemplates.length,
    searchTerm,
    typeFilter
  });

  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case 'urgent': return 'bg-red-100 text-red-800';
      case 'high': return 'bg-orange-100 text-orange-800';
      case 'medium': return 'bg-yellow-100 text-yellow-800';
      case 'low': return 'bg-green-100 text-green-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  if (!isOpen) return null;

  if (!isOpen) return null;



  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg max-w-6xl w-full max-h-[90vh] overflow-hidden">
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <div>
            <h2 className="text-xl font-semibold text-gray-900">Task Templates</h2>
            <p className="text-sm text-gray-600 mt-1">
              Create and manage reusable task templates
            </p>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={handleInitializeWorkflows}
              disabled={initializingWorkflows}
              className="flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:bg-blue-400 disabled:cursor-not-allowed"
              title="Load country-specific workflow templates for your firm"
            >
              {initializingWorkflows ? (
                <>
                  <Repeat className="w-4 h-4 mr-2 animate-spin" />
                  Initializing...
                </>
              ) : (
                <>
                  <Plus className="w-4 h-4 mr-2" />
                  Load Workflows
                </>
              )}
            </button>
            <button
              onClick={() => setShowCreateModal(true)}
              className="flex items-center px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700"
            >
              <Plus className="w-4 h-4 mr-2" />
              New Template
            </button>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600"
            >
              <X className="w-6 h-6" />
            </button>
          </div>
        </div>

        {/* Search and Filter */}
        <div className="p-6 border-b border-gray-200">
          <div className="flex gap-4">
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
              <input
                type="text"
                placeholder="Search templates..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              />
            </div>
            <select
              value={typeFilter}
              onChange={(e) => setTypeFilter(e.target.value)}
              className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
            >
              <option value="all">All Types</option>
              <option value="TAX_RETURN">Tax Return</option>
              <option value="BOOKKEEPING">Bookkeeping</option>
              <option value="PAYROLL">Payroll</option>
              <option value="AUDIT">Audit</option>
              <option value="CONSULTATION">Consultation</option>
              <option value="OTHER">Other</option>
            </select>
          </div>
        </div>

        {/* Templates List */}
        <div className="max-h-96 overflow-y-auto">

          {loading ? (
            <div className="flex items-center justify-center p-8">
              <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary-600"></div>
            </div>
          ) : filteredTemplates.length > 0 ? (
            <div className="p-6">
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {filteredTemplates.map((template) => (
                  <div key={template.id} className="bg-white border border-gray-200 rounded-lg p-6 hover:shadow-md transition-shadow">
                    <div className="flex items-start justify-between mb-4">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-2">
                          <h3 className="text-lg font-medium text-gray-900">{template.name}</h3>
                          {template.isRecurring && (
                            <Repeat className="w-4 h-4 text-blue-500" />
                          )}
                        </div>
                        <p className="text-sm text-gray-600 mb-3">{template.description}</p>
                        <div className="flex items-center gap-4 text-xs text-gray-500">
                          <span className="flex items-center gap-1">
                            <Clock className="w-3 h-3" />
                            {template.estimatedHours}h
                          </span>
                          <span className="flex items-center gap-1">
                            <Users className="w-3 h-3" />
                            Used {template.usageCount} times
                          </span>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <button
                          onClick={() => handleCreateFromTemplate(template)}
                          className="p-2 text-green-600 hover:bg-green-50 rounded"
                          title="Create task from template"
                        >
                          <Plus className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => handleDuplicateTemplate(template)}
                          className="p-2 text-blue-600 hover:bg-blue-50 rounded"
                          title="Duplicate template"
                        >
                          <Copy className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => {
                            setEditingTemplate(template);
                            setShowEditModal(true);
                          }}
                          className="p-2 text-gray-600 hover:bg-gray-50 rounded"
                          title="Edit template"
                        >
                          <Edit className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => handleDeleteTemplate(template.id)}
                          className="p-2 text-red-600 hover:bg-red-50 rounded"
                          title="Delete template"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </div>

                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <span className="px-2 py-1 text-xs bg-blue-100 text-blue-800 rounded">
                          {template.type.replace('_', ' ')}
                        </span>
                        <span className={`px-2 py-1 text-xs rounded ${getPriorityColor(template.priority)}`}>
                          {template.priority}
                        </span>
                      </div>
                      <div className="flex items-center gap-1">
                        {template.tags.slice(0, 2).map((tag, index) => (
                          <span key={index} className="px-2 py-1 text-xs bg-gray-100 text-gray-600 rounded">
                            {tag}
                          </span>
                        ))}
                        {template.tags.length > 2 && (
                          <span className="text-xs text-gray-500">+{template.tags.length - 2}</span>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ) : (
            <div className="text-center py-8">
              <div className="w-12 h-12 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <Copy className="w-6 h-6 text-gray-400" />
              </div>
              <p className="text-gray-500">No templates found</p>
              <p className="text-sm text-gray-400 mt-1">
                Create your first template to get started
              </p>
            </div>
          )}
        </div>

        {/* Create Template Modal */}
        <TaskTemplateModal
          isOpen={showCreateModal}
          onClose={() => setShowCreateModal(false)}
          onSuccess={() => {
            fetchTemplates();
          }}
          mode="create"
        />

        {/* Edit Template Modal */}
        <TaskTemplateModal
          isOpen={showEditModal}
          onClose={() => {
            setShowEditModal(false);
            setEditingTemplate(null);
          }}
          onSuccess={() => {
            fetchTemplates();
          }}
          template={editingTemplate}
          mode="edit"
        />

        {/* Create Task from Template Modal */}
        {showCreateTaskModal && selectedTemplate && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-lg max-w-md w-full mx-4">
              <div className="p-6">
                <h3 className="text-lg font-medium text-gray-900 mb-4">
                  Create Task from Template
                </h3>
                <p className="text-sm text-gray-600 mb-4">
                  Creating task from: <strong>{selectedTemplate.name}</strong>
                </p>
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Select Client *
                  </label>
                  <select
                    value={selectedClientId}
                    onChange={(e) => setSelectedClientId(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                    required
                  >
                    <option value="">Select a client</option>
                    {clients.map(client => (
                      <option key={client.id} value={client.id}>
                        {client.displayName || client.legalName}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="flex justify-end gap-4">
                  <button
                    onClick={() => {
                      setShowCreateTaskModal(false);
                      setSelectedTemplate(null);
                      setSelectedClientId('');
                    }}
                    className="px-4 py-2 text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleCreateTask}
                    disabled={!selectedClientId}
                    className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Create Task
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};
